import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';

import type {
  LayoutPoint,
  ViewerEntityId,
  ViewerPosition,
} from '../contracts/models';
import {
  findPolygonSnapTarget,
  getPolygonSnapThreshold,
  type EditableLayoutPolygon,
} from '../geometry/polygonEditing';

interface PointerEventLike {
  clientX: number;
  clientY: number;
  preventDefault(): void;
  stopPropagation(): void;
}

export interface UsePolygonEditingOptions<
  TId extends ViewerEntityId = ViewerEntityId,
> {
  enabled: boolean;
  polygons: readonly EditableLayoutPolygon<TId>[];
  pendingPoints?: ReadonlyMap<TId, readonly (readonly number[])[]>;
  scale: number;
  getContentPoint(event: Pick<PointerEventLike, 'clientX' | 'clientY'>): ViewerPosition;
  onPolygonUpdate?: (
    polygonId: TId,
    points: readonly LayoutPoint[],
  ) => void;
}

export interface UsePolygonEditingResult<
  TId extends ViewerEntityId = ViewerEntityId,
> {
  editingPolygonId: TId | null;
  draggingNodeIndex: number | null;
  snapPosition: ViewerPosition | null;
  showSnapIndicator: boolean;
  handlePolygonEditClick(
    polygonId: TId,
    event: Pick<PointerEventLike, 'stopPropagation'>,
  ): void;
  handleNodeDragStart(
    event: Pick<
      PointerEventLike,
      'preventDefault' | 'stopPropagation'
    >,
    nodeIndex: number,
  ): void;
  handleNodeDragEnd(): void;
}

export const usePolygonEditing = <
  TId extends ViewerEntityId = ViewerEntityId,
>({
  enabled,
  polygons,
  pendingPoints,
  scale,
  getContentPoint,
  onPolygonUpdate,
}: UsePolygonEditingOptions<TId>): UsePolygonEditingResult<TId> => {
  const [editingPolygonId, setEditingPolygonId] =
    useState<TId | null>(null);
  const [draggingNodeIndex, setDraggingNodeIndex] =
    useState<number | null>(null);
  const [snapPosition, setSnapPosition] =
    useState<ViewerPosition | null>(null);

  const polygonsById = useMemo(
    () =>
      new Map(
        polygons.map((polygon) => [
          String(polygon.id),
          polygon,
        ] as const),
      ),
    [polygons],
  );

  const handlePolygonEditClick = useCallback(
    (
      polygonId: TId,
      event: Pick<PointerEventLike, 'stopPropagation'>,
    ) => {
      if (!enabled) return;
      event.stopPropagation();
      setEditingPolygonId((current) =>
        current != null && String(current) === String(polygonId)
          ? null
          : polygonId,
      );
    },
    [enabled],
  );

  const handleNodeDragStart = useCallback(
    (
      event: Pick<
        PointerEventLike,
        'preventDefault' | 'stopPropagation'
      >,
      nodeIndex: number,
    ) => {
      if (!enabled) return;
      event.stopPropagation();
      event.preventDefault();
      setDraggingNodeIndex(nodeIndex);
    },
    [enabled],
  );

  const handleNodeDragEnd = useCallback(() => {
    setDraggingNodeIndex(null);
    setSnapPosition(null);
  }, []);

  const handleNodeDrag = useCallback(
    (event: MouseEvent | ReactMouseEvent) => {
      if (
        draggingNodeIndex === null ||
        editingPolygonId === null
      ) {
        return;
      }
      const polygon = polygonsById.get(String(editingPolygonId));
      if (!polygon) return;

      const cursor = getContentPoint(event);
      const snapTarget = findPolygonSnapTarget({
        cursor,
        polygons,
        pendingPoints,
        excludePolygonId: editingPolygonId,
        threshold: getPolygonSnapThreshold(scale),
      });
      const finalPosition = snapTarget ?? cursor;
      setSnapPosition(snapTarget);

      const currentPoints =
        pendingPoints?.get(editingPolygonId) ?? polygon.points;
      const nextPoints = currentPoints
        .map((point) => [Number(point[0]), Number(point[1])] as LayoutPoint);
      if (!nextPoints[draggingNodeIndex]) return;
      nextPoints[draggingNodeIndex] = [
        finalPosition.x,
        finalPosition.y,
      ];
      onPolygonUpdate?.(editingPolygonId, nextPoints);
    },
    [
      draggingNodeIndex,
      editingPolygonId,
      getContentPoint,
      onPolygonUpdate,
      pendingPoints,
      polygons,
      polygonsById,
      scale,
    ],
  );

  useEffect(() => {
    if (!enabled) {
      setEditingPolygonId(null);
      handleNodeDragEnd();
      return;
    }
    if (
      editingPolygonId !== null &&
      !polygonsById.has(String(editingPolygonId))
    ) {
      setEditingPolygonId(null);
    }
  }, [
    editingPolygonId,
    enabled,
    handleNodeDragEnd,
    polygonsById,
  ]);

  useEffect(() => {
    if (draggingNodeIndex === null) return;

    const handleMouseMove = (event: MouseEvent) => {
      event.preventDefault();
      handleNodeDrag(event);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleNodeDragEnd);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleNodeDragEnd);
    };
  }, [draggingNodeIndex, handleNodeDrag, handleNodeDragEnd]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (draggingNodeIndex !== null) {
        handleNodeDragEnd();
      } else {
        setEditingPolygonId(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () =>
      document.removeEventListener('keydown', handleKeyDown);
  }, [draggingNodeIndex, enabled, handleNodeDragEnd]);

  return {
    editingPolygonId,
    draggingNodeIndex,
    snapPosition,
    showSnapIndicator: snapPosition !== null,
    handlePolygonEditClick,
    handleNodeDragStart,
    handleNodeDragEnd,
  };
};
